import * as React from 'react'
import { ComposedChart } from 'recharts'

import { RedGreenText } from '../../../components/RedGreenText'
import { Bar } from '../../../components/chart/Bar'
import { CartesianGrid } from '../../../components/chart/CartesianGrid'
import { ChartBox } from '../../../components/chart/ChartBox'
import { ChartWrapper } from '../../../components/chart/ChartWrapper'
import { Label } from '../../../components/chart/Label'
import { Line } from '../../../components/chart/Line'
import { Tooltip, TooltipRow } from '../../../components/chart/Tooltip'
import { DateFormat, XAxis, dateAxisFormatter } from '../../../components/chart/XAxis'
import { YAxis } from '../../../components/chart/YAxis'
import { getMonthlyTicks } from '../../../components/chart/utils'
import { dateStrToTimestamp, shortFormatDollar } from '../../../util'
import { parseRouteInitData } from '../../../util-routing'
import { QuoteContext } from '../QuoteContext'

interface FundFlowsRow {
  date: string
  aum: number | null
  flow: number | null
}

enum FlowsChartDataKeys {
  Aum = 'aum',
  Flows = 'flow',
}

const AXIS_LABEL_OFFSET = 38

function tooltipValueFormatter(value: number, { dataKey }: { dataKey: string }) {
  const formatted = shortFormatDollar(value)
  if (dataKey === FlowsChartDataKeys.Aum) return formatted

  return (
    <RedGreenText value={value} rightContent={null}>
      {formatted}
    </RedGreenText>
  )
}

export function FundFlowsChart(props: React.HTMLProps<HTMLDivElement>) {
  const { index } = React.useContext(QuoteContext)
  const initData = React.useMemo(() => {
    const routeData = parseRouteInitData<FundFlowsRow[]>({
      elementId: `route-init-data-fundflows-${index}`,
      removeElement: false,
    })

    return routeData?.map((row) => ({ ...row, date: dateStrToTimestamp(row.date) }))
  }, [index])

  const monthlyTicks = React.useMemo(
    () => (initData?.length ? getMonthlyTicks(initData[0].date, initData[initData?.length - 1].date) : undefined),
    [initData]
  )

  if (!initData?.length) return null

  return (
    <ChartBox {...props} label="Fund Flows">
      <ChartWrapper height={300} isHoverHighlight={false}>
        <ComposedChart data={initData} margin={{ left: 20, right: 20 }}>
          <CartesianGrid />
          <XAxis
            domain={['dataMin', 'dataMax']}
            ticks={monthlyTicks}
            tickFormatter={(value) => dateAxisFormatter({ value, outFormat: DateFormat.MonthShort, breakOnYear: true })}
          />
          <YAxis yAxisId="aumAxis">
            <Label dx={-AXIS_LABEL_OFFSET} color="blue">
              Assets Under Management
            </Label>
          </YAxis>
          <YAxis
            tickCount={null}
            yAxisId="flowAxis"
            orientation="right"
            domain={([dataMin, dataMax]) => {
              const absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax)) * 1.2
              return [-absMax, absMax]
            }}
          >
            <Label dx={AXIS_LABEL_OFFSET}>Flow</Label>
          </YAxis>
          <Tooltip
            formatLabel={(value) => dateAxisFormatter({ value, outFormat: DateFormat.MonthShortDayNumYearFull })}
            formatValue={tooltipValueFormatter}
          >
            <TooltipRow label="Assets Under Management" dataKey={FlowsChartDataKeys.Aum} />
            <TooltipRow label="Flow" dataKey={FlowsChartDataKeys.Flows} />
          </Tooltip>

          <Line dataKey={FlowsChartDataKeys.Aum} yAxisId="aumAxis" />
          <Bar<number>
            yAxisId="flowAxis"
            dataKey={FlowsChartDataKeys.Flows}
            radius={[1, 1, 0, 0]}
            color={(value) => (value == null ? 'blue' : value > 0 ? 'green' : 'red')}
          />
        </ComposedChart>
      </ChartWrapper>
    </ChartBox>
  )
}
